import React, { useCallback } from 'react';

import {
  WpDropdown,
  WpDropdownToggle,
  WpDropdownMenu,
  WpDropdownMenuItem,
  WpDropdownMenuDivider,
  WpDropdownMenuTitle,
} from 'Wpds/components/dropdown';
import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { getSourcePage } from 'Shared/utils/getSourcePage';

import { GoalItem } from 'Shared/constants/types';

import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Constants
import Colors from 'Wpds/colors/ColorConstants';

const preventBubbleEvent = e => e.stopPropagation();

type GoalCardDropdownProps = {
  slug: string;
  goal: GoalItem;
  handleMarkComplete: Function;
  handleMarkIncomplete: Function;
  handleChangeStatus: Function;
  redirectToGoalDetailPage: Function;
};

export default function GoalCardDropdown({
  slug,
  goal,
  handleMarkComplete,
  handleMarkIncomplete,
  handleChangeStatus,
  redirectToGoalDetailPage,
}: GoalCardDropdownProps) {
  const { tr } = useTranslation();
  const isComplete = goal.complete;

  const { id: userId } = useCurrentUser();
  const canChangeStatus = userId === goal.assigneeId || userId === goal.assignerId;

  const handleSetStatusClick = useCallback(
    status => {
      handleChangeStatus(slug, status);
    },
    [handleChangeStatus, slug]
  );

  const handleMarkCompleteActionClick = useCallback(() => {
    handleMarkComplete(slug);
  }, [handleMarkComplete, slug]);

  const handleMarkIncompleteActionClick = useCallback(() => {
    handleMarkIncomplete(slug);
  }, [handleMarkIncomplete, slug]);

  const handleViewDetailsActionClick = useCallback(() => {
    redirectToGoalDetailPage(slug);
  }, [redirectToGoalDetailPage, slug]);

  const trackDropdownClick = (isOpen: boolean) => {
    if (isOpen)
      (window as any).analytics.track('Opened Item Menu', { source_page: getSourcePage(), item_type: 'goalCard' });
  };

  return (
    <div
      onClick={e => {
        preventBubbleEvent(e);
      }}
    >
      <WpDropdown placement="bottom-end" onClickCallback={trackDropdownClick}>
        <WpDropdownToggle>
          <WpButton size="sm" variant="tertiary" color="primary" qaTest="btn-goal-card-dropdown">
            <WpIcon name="ellipsis-h-alt" />
          </WpButton>
        </WpDropdownToggle>
        <WpDropdownMenu>
          {canChangeStatus && !isComplete && (
            <>
              <WpDropdownMenuTitle title={tr('perform.goals.dropdown.set_status')} />
              <WpDropdownMenuItem
                onClick={() => handleSetStatusClick('on_track')}
                qaTest="goal-card-dropdown-status-change-on-track"
              >
                <WpIcon prefix="fas" name="circle" color={Colors.green[500]} />
                {tr('perform.goals.status.on_track')}
              </WpDropdownMenuItem>
              <WpDropdownMenuItem
                onClick={() => handleSetStatusClick('at_risk')}
                qaTest="goal-card-dropdown-status-change-at-risk"
              >
                <WpIcon prefix="fas" name="circle" color={Colors.yellow[500]} />
                {tr('perform.goals.status.at_risk')}
              </WpDropdownMenuItem>
              <WpDropdownMenuItem
                onClick={() => handleSetStatusClick('missed')}
                qaTest="goal-card-dropdown-status-change-missed"
              >
                <WpIcon prefix="fas" name="circle" color={Colors.red[500]} />
                {tr('perform.goals.status.missed')}
              </WpDropdownMenuItem>
              <WpDropdownMenuItem
                onClick={() => handleSetStatusClick('achieved')}
                qaTest="goal-card-dropdown-status-change-achieved"
              >
                <WpIcon prefix="fas" name="circle" color={Colors.purple[500]} />
                {tr('perform.goals.status.achieved')}
              </WpDropdownMenuItem>
              <WpDropdownMenuItem
                onClick={() => handleSetStatusClick('no_status')}
                qaTest="goal-card-dropdown-status-change-no-status"
              >
                <WpIcon prefix="fas" name="circle" color={Colors.blue[500]} />
                {tr('perform.goals.status.no_status')}
              </WpDropdownMenuItem>
              <WpDropdownMenuDivider />
            </>
          )}
          {canChangeStatus && (
            <>
              {isComplete ? (
                <WpDropdownMenuItem onClick={handleMarkIncompleteActionClick}>
                  {tr('perform.goals.actions.mark_incomplete')}
                </WpDropdownMenuItem>
              ) : (
                <WpDropdownMenuItem onClick={handleMarkCompleteActionClick}>
                  {tr('perform.goals.actions.mark_complete')}
                </WpDropdownMenuItem>
              )}
            </>
          )}
          <WpDropdownMenuItem onClick={handleViewDetailsActionClick}>
            {tr('perform.goals.actions.view_details')}
          </WpDropdownMenuItem>
        </WpDropdownMenu>
      </WpDropdown>
    </div>
  );
}
